<template>
  <VueFinalModal
    v-model="open"
    class="flex items-center justify-center"
    :class="{
      'fixed inset-0': isFixed,
      'size-full': isFullSize,
    }"
    :focus-trap="focusTrap"
    :content-class="[
      'z-10 flex overflow-hidden box-border shadow-on-top bg-bgr-primary',
      {
        'opacity-0': !opaque,
        absolute: !!calculatePosition,
        'border border-bdr-subtle': !isFullSize,
        'size-full': isFullSize,
        fixed: isFixed && !isFullSize,
        'rounded-[10px]': !isFullSize,
      },
    ]"
    :content-style="{
      'max-height': maxHeight,
      'min-width': minWidth,
      'max-width': maxWidth,
      height: height,
      width: width,
    }"
    overlay-class="touch-none pointer-events-none bg-modal-bgr-overlay"
    :content-transition="!!calculatePosition ? null : { name: 'modal' }"
    :overlay-transition="!!calculatePosition ? null : { name: 'overlay' }"
    :hide-overlay="!overlay"
    :click-to-close="clickToClose"
    :esc-to-close="escToClose"
    @opened="onOpened"
    @closed="onClosed"
  >
    <div ref="modalContent" class="relative flex w-full flex-col">
      <!-- Header slot -->
      <ModalHeader v-if="$slots.header || title" :title="title" :has-close="btnToClose" @close="onClosed">
        <slot name="header" />
      </ModalHeader>

      <!-- Main content -->
      <div
        class="modal-content w-full flex-auto overflow-y-auto"
        :class="{
          'p-3': contentPadding,
          ...contentClass,
        }"
      >
        <slot />
      </div>

      <!-- Footer slot -->
      <slot name="footer" />
    </div>
  </VueFinalModal>
</template>

<script setup lang="ts">
  import { useTemplateRef } from "#imports";
  import { VueFinalModal } from "vue-final-modal";
  import type { Options as FocusTrap } from "focus-trap";

  defineOptions({
    name: "Modal",
  });

  type SizeType = "dynamic" | "fixed" | "full";

  const {
    btnToClose = true,
    calculatePosition = undefined,
    clickToClose = true,
    contentClass = {},
    contentPadding = true,
    escToClose = true,
    focusTrap = false,
    height = undefined,
    maxHeight = "100%",
    maxWidth = undefined,
    minWidth = undefined,
    overlay = true,
    size = "dynamic",
    title = "",
    width = undefined,
  } = defineProps<{
    btnToClose?: boolean;
    calculatePosition?: (cb: () => void) => void;
    clickToClose?: boolean;
    contentClass?: Record<string, boolean>;
    contentPadding?: boolean;
    escToClose?: boolean;
    focusTrap?: false | FocusTrap;
    height?: string;
    maxHeight?: string;
    maxWidth?: string;
    minWidth?: string;
    overlay?: boolean;
    size?: SizeType;
    title?: string;
    width?: string;
  }>();

  const open = defineModel<boolean>("open", { default: false });
  const emit = defineEmits(["update:open", "opened", "closed", "confirm"]);
  const closingDuration = 250;
  const openingDuration = 900;
  const opaque = ref(false);

  const isFullSize = computed(() => size === "full");
  const isFixed = computed(() => size === "fixed" || size === "full");

  const modalContent = useTemplateRef<HTMLElement>("modalContent");
  const modal = ref<HTMLElement | null>(null);
  const modalHeight = ref(0);
  const modalWidth = ref(0);

  // Lifecycle hooks
  onMounted(() => {
    document.addEventListener("keydown", handleEscKey);
  });

  onUnmounted(() => {
    document.removeEventListener("keydown", handleEscKey);
    if (calculatePosition) {
      window.removeEventListener("resize", calculatePosition);
    }
  });

  // Methods
  const handleEscKey = (event: KeyboardEvent) => {
    if (event.key === "Escape" && open.value && escToClose) {
      open.value = false;
    }
  };

  const onOpened = () => {
    // Set modal ref to modalContent's parent element
    modal.value = modalContent.value?.parentElement || null;
    getHeight();
    getWidth();

    if (calculatePosition) {
      window.addEventListener("resize", calculatePosition);
    }

    if (calculatePosition) calculatePosition(() => setOpaque(true));
    else setOpaque(true);
    emit("opened");
  };

  const setOpaque = (value: boolean) => {
    opaque.value = value;
  };

  const onClosed = () => {
    if (calculatePosition) {
      window.removeEventListener("resize", calculatePosition);
    }
    setOpaque(false);
    open.value = false;
    emit("closed");
  };

  const getHeight = () => {
    if (!modalContent.value) return 0;

    const children = modalContent.value.children as HTMLCollectionOf<HTMLElement>;

    let h = 2;
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (child) {
        h += child.clientHeight;
      }
    }

    // Get padding of modalContent
    const style = window.getComputedStyle(modalContent.value);
    h += parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);

    modalHeight.value = h;
    return h;
  };

  const getWidth = () => {
    if (!modal.value) return 0;

    const w = modal.value.offsetWidth;
    modalWidth.value = w;
    return w;
  };

  const updateHeight = (newHeight?: number | null) => {
    nextTick(() => {
      const h = newHeight || getHeight();
      setStyleProperty("height", h + "px");
    });
  };

  const setStyleProperty = (property: string, value: string) => {
    if (modal.value) {
      modal.value.style.setProperty(property, value);
    }
  };

  defineExpose({
    closingDuration,
    openingDuration,
    height: modalHeight,
    width: modalWidth,
    open,
    modal,
    updateHeight,
    setStyleProperty,
  });
</script>
